import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { EventOverviewAttendee, SessionAttendanceStatus, OwnerEventOverview, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';

const ActivityOverviewPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState<OwnerEventOverview | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SessionAttendanceStatus>('all');
  const [updatingRowKey, setUpdatingRowKey] = useState('');
  const [updatingPaymentRowKey, setUpdatingPaymentRowKey] = useState('');
  const [mobileSection, setMobileSection] = useState<'register' | 'details'>('register');

  const loadOverview = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError('');
    try {
      const data = await clubsBookingApi.getMyEventOverview(eventId);
      setOverview(data);
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load activity overview');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!overview?.sessions?.length) {
      setSelectedSessionId('');
      return;
    }

    const requestedSessionId = searchParams.get('sessionId');
    if (requestedSessionId && overview.sessions.some((session) => session.id === requestedSessionId)) {
      if (selectedSessionId !== requestedSessionId) {
        setSelectedSessionId(requestedSessionId);
      }
      return;
    }

    if (!selectedSessionId || !overview.sessions.some((session) => session.id === selectedSessionId)) {
      setSelectedSessionId(overview.sessions[0].id);
    }
  }, [overview, searchParams, selectedSessionId]);

  const formatDateTime = useMemo(
    () =>
      new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [],
  );

  const selectedSession = useMemo(
    () => overview?.sessions.find((session) => session.id === selectedSessionId) || null,
    [overview, selectedSessionId],
  );
  const selectedSessionCapacityLabel = selectedSession
    ? (selectedSession.capacity < 0 ? 'Unlimited' : String(selectedSession.capacity))
    : '0';
  const selectedSessionSpotsRemainingLabel = selectedSession
    ? (selectedSession.capacity < 0 ? 'Unlimited' : String(selectedSession.spotsRemaining))
    : '0';
  const formatSessionOptionLabel = (session: OwnerEventOverview['sessions'][number]) => {
    const dateLabel = formatDateTime.format(new Date(session.startDateTime));
    if (session.capacity < 0) {
      return `${dateLabel} - unlimited`;
    }
    const spotsRemaining = Math.max(0, Number(session.spotsRemaining || 0));
    return `${dateLabel} - ${spotsRemaining} spot${spotsRemaining === 1 ? '' : 's'}`;
  };

  const filteredAttendees = useMemo(() => {
    if (!selectedSession) return [];
    const query = searchTerm.trim().toLowerCase();
    return selectedSession.attendees.filter((attendee) => {
      if (statusFilter !== 'all' && attendee.sessionAttendanceStatus !== statusFilter) return false;
      if (!query) return true;
      return (
        attendee.childName.toLowerCase().includes(query) ||
        attendee.parentName.toLowerCase().includes(query) ||
        String(attendee.parentEmail || '').toLowerCase().includes(query)
      );
    });
  }, [selectedSession, searchTerm, statusFilter]);

  const rowKey = (attendee: EventOverviewAttendee) => `${attendee.bookingId}:${attendee.childIndex}`;

  const updateSessionAttendance = async (attendee: EventOverviewAttendee, status: SessionAttendanceStatus) => {
    if (!selectedSessionId) return;
    const key = rowKey(attendee);
    setUpdatingRowKey(key);
    setError('');
    try {
      await clubsBookingApi.updateBookingSessionAttendance(attendee.bookingId, {
        sessionId: selectedSessionId,
        childIndex: attendee.childIndex,
        status,
      });
      await loadOverview();
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not update attendance');
    } finally {
      setUpdatingRowKey('');
    }
  };

  const updatePaymentStatus = async (attendee: EventOverviewAttendee, status: EventOverviewAttendee['paymentStatus']) => {
    if (attendee.paymentMethod === 'card') return;
    const key = rowKey(attendee);
    setUpdatingPaymentRowKey(key);
    setError('');
    try {
      await clubsBookingApi.updateBookingPayment(attendee.bookingId, { paymentStatus: status });
      await loadOverview();
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not update payment');
    } finally {
      setUpdatingPaymentRowKey('');
    }
  };

  const statusBadge = (attendee: EventOverviewAttendee) => {
    if (attendee.sessionAttendanceStatus === 'checked_in') {
      const at = attendee.checkedInAt
        ? new Date(attendee.checkedInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '';
      return { text: `Checked in${at ? ` at ${at}` : ''}`, className: 'bg-emerald-100 text-emerald-700' };
    }
    if (attendee.sessionAttendanceStatus === 'checked_out') {
      const checkedInAt = attendee.checkedInAt
        ? new Date(attendee.checkedInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '';
      const checkedOutAt = attendee.checkedOutAt
        ? new Date(attendee.checkedOutAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '';
      if (checkedInAt && checkedOutAt) {
        return { text: `Checked in ${checkedInAt} | out ${checkedOutAt}`, className: 'bg-blue-100 text-blue-700' };
      }
      return { text: `Checked out${checkedOutAt ? ` at ${checkedOutAt}` : ''}`, className: 'bg-blue-100 text-blue-700' };
    }
    if (attendee.sessionAttendanceStatus === 'absent') {
      return { text: 'Marked absent', className: 'bg-rose-100 text-rose-700' };
    }
    return { text: 'Not arrived', className: 'bg-slate-200 text-slate-700' };
  };

  const isCashPaymentMethod = (attendee: EventOverviewAttendee) => attendee.paymentMethod !== 'card';
  const paymentTypeLabel = (attendee: EventOverviewAttendee) => (isCashPaymentMethod(attendee) ? 'Cash' : 'Online card');

  const renderAttendanceActions = (attendee: EventOverviewAttendee) => {
    const key = rowKey(attendee);
    const isAttendanceUpdating = updatingRowKey === key;
    const isPaymentUpdating = updatingPaymentRowKey === key;
    const isUpdating = isAttendanceUpdating || isPaymentUpdating;
    const primaryAction =
      attendee.sessionAttendanceStatus === 'checked_in'
        ? { label: 'Check out', nextStatus: 'checked_out' as SessionAttendanceStatus }
        : attendee.sessionAttendanceStatus === 'checked_out'
          ? null
          : { label: 'Check in', nextStatus: 'checked_in' as SessionAttendanceStatus };
    const paymentAction = isCashPaymentMethod(attendee)
      ? {
          label: attendee.paymentStatus === 'paid' ? 'Mark unpaid' : 'Mark paid',
          nextStatus: attendee.paymentStatus === 'paid' ? ('pending' as const) : ('paid' as const),
        }
      : null;

    return (
      <div className="flex items-center gap-2">
        {primaryAction && (
          <button
            type="button"
            disabled={isUpdating}
            onClick={() => void updateSessionAttendance(attendee, primaryAction.nextStatus)}
            className="rounded-md bg-[#1F8F5F] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#1F8F5F] disabled:opacity-60 md:px-3"
          >
            {isAttendanceUpdating ? 'Saving...' : primaryAction.label}
          </button>
        )}
        <details className="relative">
          <summary className="cursor-pointer list-none rounded-md border border-slate-300 px-2 py-1 text-slate-500 hover:bg-slate-50">...</summary>
          <div className="absolute right-0 z-20 mt-1 w-40 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100"
              onClick={() => void updateSessionAttendance(attendee, 'not_arrived')}
              disabled={isUpdating}
            >
              Reset
            </button>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100"
              onClick={() => void updateSessionAttendance(attendee, 'absent')}
              disabled={isUpdating}
            >
              Mark absent
            </button>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100"
              onClick={() => void updateSessionAttendance(attendee, 'checked_in')}
              disabled={isUpdating}
            >
              Check in
            </button>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100"
              onClick={() => void updateSessionAttendance(attendee, 'checked_out')}
              disabled={isUpdating}
            >
              Check out
            </button>
            {paymentAction && (
              <>
                <div className="my-1 border-t border-slate-200" />
                <button
                  type="button"
                  data-testid={`activity-overview-payment-toggle-${rowKey(attendee)}`}
                  className="w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-100"
                  onClick={() => void updatePaymentStatus(attendee, paymentAction.nextStatus)}
                  disabled={isUpdating}
                >
                  {isPaymentUpdating ? 'Saving...' : paymentAction.label}
                </button>
              </>
            )}
          </div>
        </details>
      </div>
    );
  };

  return (
    <AdminShell
      title={overview?.event.title || 'Activity overview'}
      mobileTopRight={(
        <Link to="/activities" className="text-sm font-medium text-[#1F8F5F] hover:underline">
          Back
        </Link>
      )}
    >
      <div className="mb-2 hidden md:flex justify-end">
        <Link to="/activities" className="text-sm text-[#1F8F5F] hover:underline">
          Back
        </Link>
      </div>

      {loading && <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading activity overview...</p>}
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {!loading && overview && (
        <div className="space-y-3 md:space-y-4">
          <div className="md:hidden">
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 text-sm">
              <button
                type="button"
                onClick={() => setMobileSection('register')}
                className={`rounded-md px-3 py-1.5 font-medium ${mobileSection === 'register' ? 'bg-[#1F8F5F] text-white' : 'text-slate-600'}`}
              >
                Register
              </button>
              <button
                type="button"
                onClick={() => setMobileSection('details')}
                className={`rounded-md px-3 py-1.5 font-medium ${mobileSection === 'details' ? 'bg-[#1F8F5F] text-white' : 'text-slate-600'}`}
              >
                Details
              </button>
            </div>
          </div>

          {selectedSession && (
            <section className={`${mobileSection === 'register' ? 'block' : 'hidden'} md:block rounded-xl border border-slate-200 bg-white p-3 sm:p-4 md:p-6`}>
              <div className="mb-3 grid gap-2 md:grid-cols-[1fr_360px] md:items-end">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Session register</p>
                  <h2 className="text-lg font-semibold leading-tight text-slate-900 md:text-xl">
                    {formatDateTime.format(new Date(selectedSession.startDateTime))} to{' '}
                    {new Date(selectedSession.endDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </h2>
                  <p className="mt-0.5 text-sm text-slate-600">
                    {selectedSession.bookedSpots}/{selectedSessionCapacityLabel} booked - {selectedSessionSpotsRemainingLabel} spots remaining
                  </p>
                </div>
                <label className="text-sm text-slate-600">
                  Session
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={selectedSessionId}
                    onChange={(e) => {
                      const nextSessionId = e.target.value;
                      setSelectedSessionId(nextSessionId);
                      setSearchParams({ sessionId: nextSessionId }, { replace: true });
                    }}
                  >
                    {overview.sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {formatSessionOptionLabel(session)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mb-3 flex justify-end">
                <Link
                  to={`/activities/${overview.event.id}/walk-in?sessionId=${encodeURIComponent(selectedSessionId)}`}
                  className="w-full rounded-lg bg-[#1F8F5F] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#18724c] md:w-auto"
                  data-testid="activity-overview-add-walk-in"
                >
                  Add walk-in attendee
                </Link>
              </div>

                <div className="mb-3 grid grid-cols-3 gap-1.5 sm:gap-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center md:p-3">
                    <p className="text-xl font-semibold leading-none text-slate-900 md:text-lg">{selectedSession.checkedInCount}</p>
                    <p className="mt-1 text-[11px] text-slate-500 md:text-xs">Checked in</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center md:p-3">
                    <p className="text-xl font-semibold leading-none text-slate-900 md:text-lg">{selectedSession.checkedOutCount}</p>
                    <p className="mt-1 text-[11px] text-slate-500 md:text-xs">Checked out</p>
                  </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center md:p-3">
                  <p className="text-xl font-semibold leading-none text-slate-900 md:text-lg">{selectedSession.absentCount}</p>
                  <p className="mt-1 text-[11px] text-slate-500 md:text-xs">Absent</p>
                </div>
              </div>

              <div className="mb-3 grid gap-2 md:grid-cols-[1fr_180px]">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="all">Any status</option>
                  <option value="not_arrived">Not arrived</option>
                  <option value="checked_in">Checked in</option>
                  <option value="checked_out">Checked out</option>
                  <option value="absent">Absent</option>
                </select>
              </div>

              <div className="space-y-1.5 md:hidden">
                {filteredAttendees.map((attendee, index) => {
                  const badge = statusBadge(attendee);
                  return (
                    <article key={`${rowKey(attendee)}:${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[15px] font-semibold leading-tight text-[#1F8F5F]">{attendee.childName}</p>
                          <p className="text-xs leading-tight text-slate-500">{attendee.parentName}</p>
                        </div>
                        {renderAttendanceActions(attendee)}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                        <span className={`rounded px-2 py-1 font-medium ${badge.className}`}>{badge.text}</span>
                        <span className={`rounded px-2 py-1 font-medium ${attendee.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {attendee.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        Payment type: <span className="font-medium text-slate-700">{paymentTypeLabel(attendee)}</span>
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                        <Link
                          to={`/bookings?eventId=${overview.event.id}&bookingId=${attendee.bookingId}`}
                          className="ml-auto rounded px-2 py-1 font-medium text-[#1F8F5F] underline-offset-2 hover:underline"
                        >
                          Booking
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="hidden rounded-lg border border-slate-200 md:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Attendee</th>
                      <th className="px-3 py-2">Parent</th>
                      <th className="px-3 py-2">Ticket</th>
                      <th className="px-3 py-2">Add-ons</th>
                      <th className="px-3 py-2">Payment</th>
                      <th className="px-3 py-2">Payment type</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Actions</th>
                      <th className="px-3 py-2">Booking</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAttendees.map((attendee, index) => {
                      const badge = statusBadge(attendee);
                      return (
                        <tr key={`${rowKey(attendee)}:${index}`}>
                          <td className="px-3 py-2.5">
                            <div className="font-medium">{attendee.childName}</div>
                            {typeof attendee.childAge === 'number' && <div className="text-xs text-slate-500">Age {attendee.childAge}</div>}
                          </td>
                          <td className="px-3 py-2.5">
                            <div>{attendee.parentName}</div>
                            {/*<div className="text-xs text-slate-500">{attendee.parentEmail}</div>*/}
                          </td>
                          <td className="px-3 py-2.5">{attendee.ticketName}</td>
                          <td className="px-3 py-2.5">{attendee.addOns.length ? attendee.addOns.join(', ') : <span className="text-slate-400">None</span>}</td>
                          <td className="px-3 py-2.5">
                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${attendee.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {attendee.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-sm text-slate-700">{paymentTypeLabel(attendee)}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${badge.className}`}>{badge.text}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            {renderAttendanceActions(attendee)}
                          </td>
                          <td className="px-3 py-2.5">
                            <Link to={`/bookings?eventId=${overview.event.id}&bookingId=${attendee.bookingId}`} className="text-[#1F8F5F] hover:underline">
                              View
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredAttendees.length === 0 && (
                <p className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                  No attendees match your current filters.
                </p>
              )}
            </section>
          )}

          <section className={`${mobileSection === 'details' ? 'block' : 'hidden'} md:block rounded-xl border border-slate-200 bg-white p-3 sm:p-4 md:p-6`}>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Activity details</h3>
            <div className="mt-2 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Location</p>
                <p className="mt-0.5 line-clamp-2 font-medium leading-snug text-slate-800">{overview.event.location || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                <p className="mt-0.5 font-medium text-slate-800">{overview.event.status === 'published' ? 'Live' : 'Not live'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Sessions</p>
                <p className="mt-0.5 font-medium text-slate-800">{overview.sessions.length}</p>
              </div>
            </div>
          </section>

          {overview.sessions.length === 0 && (
            <section className={`${mobileSection === 'register' ? 'block' : 'hidden'} md:block rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500`}>
              This activity has no sessions yet.
            </section>
          )}
        </div>
      )}

    </AdminShell>
  );
};

export default ActivityOverviewPage;

